import { createRoot } from "react-dom/client";
import "./index.css";
import "./components-v2/index.css";
import "./cookies/cookies.css";
import App from "./App";
import { createBrowserHistory } from "history";
import { ApiService, createAxiosInstance, isAxiosError } from "./api";
import { RootStore } from "./stores/RootStore";
import { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import * as updater from "./updater";
import { updateIfNewVersionAvailable } from "./updater";
import { isProductionEnvironment, isTestEnvironment } from "./utils/environments";
import { TooltipManager } from "./components-v2/Tooltip";
import { ErrorInfo } from "react";
import { logError } from "./utils/log";
import { ArcElement, Chart, Tooltip } from "chart.js";
import { get } from "lodash";
import { initI18n } from "./i18n/i18nDomain";
import { DispatchContext, RootStoreContext } from "./stores/RootStoreContext";
import { handleLinks, startRouter } from "./routes/router";
import { routes } from "./routes/routes";
import { pollAnnouncements } from "./announcement/announcementDomain";

initSentry();

const history = createBrowserHistory();
const axiosInstance = createAxiosInstance();
const apiService = new ApiService(axiosInstance);
const rootStore = new RootStore(axiosInstance, apiService, history);

const handleRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  Error.captureStackTrace(config);
  if (config.headers) {
    config.headers["X-Language"] = rootStore.i18n.lang;
  }
  return config;
};

const handleRequestError = (err: unknown) => {
  if (isAxiosError(err) && err.config && "stack" in err.config) {
    err.stack = String(err.config.stack);
  }
  return Promise.reject(err);
};

const handleResponse = (response: AxiosResponse) => {
  updater.checkVersion(response.headers["x-version"]);
  return response;
};

const handleResponseError = (err: unknown) => {
  if (isAxiosError(err)) {
    if (err.config && "stack" in err.config) {
      err.stack = String(err.config?.stack);
    }
    updater.checkVersion(err.response?.headers["x-version"]);
    const status = err.response?.status;
    if (status === 401) {
      rootStore.userStore.handleAuthError(err);
    } else if (status && status >= 500) {
      logError(err, `Server error: ${err.message}, ${err.config?.url}`);
    }
  }
  return Promise.reject(err);
};

const handleUncaughtError = (error: Error, errorInfo: ErrorInfo) => {
  if (
    !error.message.startsWith("Failed to fetch dynamically imported module") ||
    !updateIfNewVersionAvailable()
  ) {
    logError(error, "Uncaught error", { errorInfo, userId: rootStore.userStore.user?.externalId });
  }
};

window.onerror = (message, source, lineno, colno, error) => {
  logError(error, "window.onerror", {
    message,
    source,
    lineno,
    colno,
    error,
    userId: rootStore.userStore.user?.externalId,
  });
};

window.onunhandledrejection = (event) => {
  logError(event.reason, "window.onunhandledrejection", {
    userId: rootStore.userStore.user?.externalId,
  });
};

axiosInstance.interceptors.request.use(handleRequest, handleRequestError);
axiosInstance.interceptors.response.use(handleResponse, handleResponseError);

initI18n(rootStore);
rootStore.userStore.init();
startRouter(history, routes, rootStore.setRoute);
handleLinks(history, (href) => /^\/(?!api)/.test(href));
pollAnnouncements(rootStore.dispatch);

// Hack: prevent Safari from showing logged-in page from the cache after logout
addEventListener("pageshow", () => {
  rootStore.userStore.checkAuthStatus();
});

history.listen(() => {
  updater.updateIfNewVersionAvailable();
  rootStore.messageStore.hideMessage();
  rootStore.dialogStore.closeDialog();
});

initChartJs();

createRoot(document.getElementById("root")!).render(
  <DispatchContext.Provider value={rootStore.dispatch}>
    <RootStoreContext.Provider value={rootStore}>
      <App history={history} onUncaughtError={handleUncaughtError} />
      <TooltipManager />
    </RootStoreContext.Provider>
  </DispatchContext.Provider>,
);

function initSentry() {
  if (isProductionEnvironment() || isTestEnvironment()) {
    Sentry.init({
      environment: isProductionEnvironment() ? "prod" : isTestEnvironment() ? "test" : "dev",
      dsn: "https://62f3f8f39f2e4809bc99a83e2d6c068c@o1108649.ingest.sentry.io/6136469",
      integrations: [browserTracingIntegration()],
      beforeBreadcrumb(breadcrumb) {
        // Don't log entries that are logged to the console as these are already manually logged to Sentry in log.ts
        if (breadcrumb.category === "console") {
          return null;
        }
        return breadcrumb;
      },
      beforeSend(event, hint) {
        if (/(googletagmanager)|(analytics)/.test(get(hint, ["originalException", "stack"], ""))) {
          // Don't log errors from Google Tag Manager / Analytics
          return null;
        }
        return event;
      },
      // Adjust this value in production, or using tracesSampler for finer control
      tracesSampleRate: 1.0,
      // The depth of objects/arrays to keep in the logs
      normalizeDepth: 5,
    });
  }
}

function initChartJs() {
  Chart.register(ArcElement, Tooltip);
}
